import userAuth from "./userAuth";

// [INC-1955] Centralized data deletion. One module for every endpoint under
// /api/v1/admin/deletion, so the screens never spell a path out themselves.
//
// The platform wraps every payload as { result, message }, so each call here returns
// response.data.result and lets the caller deal with the fields it needs.

const BASE = "/admin/deletion";

const unwrap = (response) => response?.data?.result;

// The tab keys the register uses. Same strings as targetType on the wire.
export const DELETION_TABS = {
  clients: "client",
  interviews: "interview",
  adminSubusers: "admin_subuser",
  clientSubusers: "client_subuser",
  // Register tab key only (not a targetType): Mock Candidates and Mock Interviews together.
  mock: "mock",
  // Mock Interview menu's Deleted Interviews page: interviews only, no candidate accounts.
  mockInterviews: "mock_interview",
};

// targetType sent when a Mock Candidate account is deleted.
export const MOCK_CANDIDATE_TARGET = "mock_candidate";

export const getIsSuperAdmin = async () => {
  const result = unwrap(await userAuth.get(`${BASE}/me`));
  return Boolean(result?.isSuperAdmin);
};

// [INC-2022] What the logged-in admin holds under Deletion Management. The Super Admin holds
// all five; any other role holds what a Super Admin granted it. The API enforces the same.
export const getDeletionPermissions = async () => {
  const result = unwrap(await userAuth.get(`${BASE}/me`));
  return {
    ...(result?.deletionPermissions || {}),
    isSuperAdmin: Boolean(result?.isSuperAdmin),
  };
};

// entity: clients | interviews | adminSubUsers | mockCandidates | deletedRegisterLogs
export const getDeletionAccess = async (entity) => {
  const permissions = await getDeletionPermissions();
  return permissions.isSuperAdmin || permissions[entity] === true;
};

// tab/status/search/from/to/page/limit - all optional, see section 5 of the contract.
export const getRegister = async (params) =>
  unwrap(await userAuth.get(`${BASE}/register`, { params }));

export const getRegisterEntry = async (id) =>
  unwrap(await userAuth.get(`${BASE}/register/${id}`));

// Same query parameters as getRegister; returns flat rows with human column names.
export const exportRegister = async (params) =>
  unwrap(await userAuth.get(`${BASE}/export`, { params }));

export const getPreview = async (targetType, targetId) =>
  unwrap(await userAuth.get(`${BASE}/preview/${targetType}/${targetId}`));

// The Interview Ownership dropdown: the parent account plus its other active sub-users.
export const getTransferCandidates = async (targetId) =>
  unwrap(await userAuth.get(`${BASE}/transfer-candidates/${targetId}`));

// :id is the REGISTER row id, not the interview id. Works after the purge.
export const getInterviewHistory = async (id) =>
  unwrap(await userAuth.get(`${BASE}/interview/${id}`));

export const getAttachment = async (id) =>
  unwrap(await userAuth.get(`${BASE}/attachment/${id}`));

/**
 * The one endpoint behind Delete Client, Delete User and Delete Interview.
 *
 * [INC-2103] requestedAt is the date & time picked in the modal and is saved as Requested
 * On. Deleted On is the server's own time, so the two are recorded independently.
 */
export const requestDeletion = async ({
  targetType,
  targetId,
  requestedByName,
  requestedByEmail,
  deletionReason,
  attachment,
  transferTo,
  requestedAt,
}) => {
  const form = new FormData();
  form.append("targetType", targetType);
  form.append("targetId", targetId);
  form.append("requestedByName", requestedByName);
  form.append("requestedByEmail", requestedByEmail);
  form.append("deletionReason", deletionReason);
  if (requestedAt) form.append("requestedAt", new Date(requestedAt).toISOString());
  form.append("attachment", attachment);
  // Omitted entirely rather than sent empty: a client or interview deletion 400s on it.
  if (transferTo) form.append("transferTo", transferTo);
  return unwrap(
    await userAuth.post(`${BASE}/request`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  );
};

export const restoreRecord = async (registerId) =>
  unwrap(await userAuth.post(`${BASE}/restore/${registerId}`));

// Irreversible. confirmText must equal the row's name - see purgeConfirmName below.
export const purgeRecord = async (registerId, { confirmText, note }) =>
  unwrap(await userAuth.post(`${BASE}/purge/${registerId}`, { confirmText, note }));

// Old-flow suspended sub-user with no register row: the server registers and purges it.
export const purgeLegacy = async ({ targetType, targetId, confirmText, note }) =>
  unwrap(
    await userAuth.post(`${BASE}/purge-legacy`, {
      targetType,
      targetId,
      confirmText,
      note,
    }),
  );

/**
 * What the admin has to type to confirm a permanent delete.
 *
 * targetName falls back to targetCompany because that is what the server compares
 * against; an interview whose candidate record carried no name has an empty targetName,
 * and asking for that alone would be asking for a value that can never match.
 */
export const purgeConfirmName = (row) =>
  (row?.targetName || row?.targetCompany || "").trim();

// A cascade child cannot be restored or purged on its own - its parent owns the action.
export const isCascadeChild = (row) => Boolean(row?.parentRegisterId);

// A purged row has had its personal fields cleared, so an empty cell is data, not a bug.
export const maskedValue = (value) => value || "—";

export const readApiError = (error, fallback) =>
  error?.response?.data?.message || fallback;
